/**
 * ETL tab — the period's generated exports (FinJournal, Data Platform).
 *
 * A row exists once the source file's *status* says the export can be generated
 * (`available`), independent of whether the bytes exist yet (`persisted`) — the
 * first download materialises them. `available && !persisted` is a normal,
 * expected state, not an error: the row renders with dashes for the metadata and
 * an enabled Download button, same as a persisted row.
 */
import type * as React from "react";
import { useCallback, useState } from "react";
import { Download } from "lucide-react";
import { Button, Loader } from "@/ui/atoms";
import { EmptyState } from "@/ui/molecules";
import { formatRelativeTime } from "@/utils/relativeTime";
import type { TFileReviewDetails } from "./types";
import type { TEtlTabUseCases } from "./useCases";
import type { TSubmissionEtlArtifact } from "./submissionContext";
import { canDownloadFileArtifact } from "./canDownloadFileArtifact";

const KIND_LABELS: Record<TSubmissionEtlArtifact["kind"], string> = {
    finjournal: "FinJournal",
    data_platform: "Data Platform",
};

export type TEtlTabProps = {
    /** The loaded file. The shell does not render this tab until it has one. */
    file: TFileReviewDetails;
    useCases: TEtlTabUseCases;
};

export const createEtlTab = () => {
    return function EtlTab({ file, useCases }: TEtlTabProps): React.ReactElement {
        const {
            useSubmissionContext,
            useFinjournalReviewCaps,
            useTbglReviewCaps,
            onDownloadGeneratedExport,
        } = useCases;

        const context = useSubmissionContext();
        const finjournalCaps = useFinjournalReviewCaps();
        const tbglCaps = useTbglReviewCaps();

        const [downloadingKind, setDownloadingKind] = useState<
            TSubmissionEtlArtifact["kind"] | null
        >(null);

        const handleDownload = useCallback(
            async (kind: TSubmissionEtlArtifact["kind"], sourceFileId: string) => {
                setDownloadingKind(kind);
                try {
                    await onDownloadGeneratedExport(kind, sourceFileId);
                } finally {
                    setDownloadingKind(null);
                }
            },
            [onDownloadGeneratedExport],
        );

        const artifacts = context.status !== "rejected" ? (context.data?.etlArtifacts ?? []) : [];
        const availableArtifacts = artifacts.filter((artifact) => artifact.available);

        // The download is gated on the *source* file, which is not always the file on
        // screen: a deal-scoped FinJournal's period export lives on the TB or GL, and
        // the backend checks that file's own download action. The context's slots
        // already carry each expected type's file id, so the type is resolvable here;
        // the fallback covers a standalone FinJournal, whose export is its own file.
        const sourceFileTypeById = new Map<string, string>(
            (context.data?.slots ?? [])
                .filter((slot) => slot.fileId !== null)
                .map((slot) => [slot.fileId as string, slot.fileType]),
        );

        if (availableArtifacts.length === 0) {
            return (
                <div data-testid="etl-tab-empty-state">
                    <EmptyState title="No export has been generated for this period." />
                </div>
            );
        }

        return (
            <div data-testid="etl-tab" className="space-y-3">
                {availableArtifacts.map((artifact) => {
                    const relative = artifact.generatedAt
                        ? formatRelativeTime(artifact.generatedAt)
                        : null;
                    const isDownloading = downloadingKind === artifact.kind;
                    const canDownload = canDownloadFileArtifact(
                        {
                            fileType:
                                sourceFileTypeById.get(artifact.sourceFileId) ?? file.fileType,
                        },
                        finjournalCaps,
                        tbglCaps,
                    );

                    return (
                        <div
                            key={artifact.kind}
                            data-testid={`etl-tab-row-${artifact.kind}`}
                            className="flex items-center justify-between gap-4 rounded-lg border px-4 py-3"
                        >
                            <div className="flex items-center gap-6">
                                <span className="text-sm font-medium">
                                    {KIND_LABELS[artifact.kind]}
                                </span>
                                <span
                                    data-testid={`etl-tab-generated-at-${artifact.kind}`}
                                    title={relative?.full}
                                    className="text-sm text-muted-foreground"
                                >
                                    {relative ? relative.label : "—"}
                                </span>
                                <span
                                    data-testid={`etl-tab-generated-by-${artifact.kind}`}
                                    className="text-sm text-muted-foreground"
                                >
                                    {artifact.generatedByName ?? "—"}
                                </span>
                            </div>

                            {canDownload && (
                                <Button
                                    variant="outline"
                                    data-testid={`etl-tab-download-${artifact.kind}`}
                                    onClick={() =>
                                        handleDownload(artifact.kind, artifact.sourceFileId)
                                    }
                                    disabled={isDownloading}
                                >
                                    {isDownloading ? (
                                        <>
                                            <Loader size="sm" className="mr-2" />
                                            Downloading...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="h-4 w-4 mr-2" />
                                            Download
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };
};
